/**
 * shared/PhotoViewer/PhotoViewer.jsx
 * ════════════════════════════════════════════════════════════════
 * Повноекранний перегляд фото і PDF.
 * Закриття: Escape, клік поза вмістом.
 *
 * ВИКОРИСТАННЯ:
 *   import PhotoViewer from '../shared/PhotoViewer/PhotoViewer';
 *
 *   const [viewer, setViewer] = useState(null);
 *
 *   // Фото:
 *   setViewer({ src: '...url...', name: 'photo.jpg', mtype: 'image/jpeg' });
 *
 *   // PDF:
 *   setViewer({ src: '...url...', name: 'document.pdf', mtype: 'application/pdf' });
 *
 *   // YouTube відео (inline iframe, без переходу на новий таб):
 *   setViewer({ src: 'https://www.youtube.com/watch?v=XXXXXXXXXXX', name: 'Назва відео', mtype: 'video/youtube' });
 *
 *   // Без mtype — визначається автоматично з src або name:
 *   setViewer({ src: '...url...', name: 'file.pdf' });
 *
 *   // JSX:
 *   {viewer && (
 *     <PhotoViewer
 *       src={viewer.src}
 *       name={viewer.name}
 *       mtype={viewer.mtype}
 *       onClose={() => setViewer(null)}
 *     />
 *   )}
 *
 * Props:
 *   src      {string}   — URL файлу
 *   name     {string?}  — підпис / назва файлу
 *   mtype    {string?}  — MIME тип (image/*, application/pdf)
 *                         якщо не вказано — визначається з name або src
 *   onClose  {function} — виклик при закритті
 * ════════════════════════════════════════════════════════════════
 */

import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark, faFilePdf, faDownload, faSpinner,
} from '@fortawesome/free-solid-svg-icons';

// ── Визначити тип файлу ───────────────────────────────────────────────────────

function detectType(src, name, mtype) {
    if (mtype) {
        if (mtype.match(/image\//i))           return 'image';
        if (mtype === 'application/pdf')        return 'pdf';
        if (mtype === 'video/youtube')          return 'video';
    }
    var filename = name || src || '';
    if (filename.match(/\.(jpg|jpeg|png|gif|webp|svg|bmp)(\?|$)/i)) return 'image';
    if (filename.match(/\.pdf(\?|$)/i))                              return 'pdf';
    // fallback — спробуємо показати як зображення
    return 'image';
}

// ── PDF viewer ────────────────────────────────────────────────────────────────

var PdfViewer = function({ src, name }) {
    var [loading, setLoading] = useState(true);
    var [error, setError]     = useState(false);

    return (
        <div style={{
            width:  'min(900px, calc(100vw - 40px))',
            height: 'calc(100vh - 100px)',
            borderRadius: 10,
            overflow: 'hidden',
            background: '#525659',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
        }}>
            {/* Шапка PDF */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px',
                background: '#3d3d3d',
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FontAwesomeIcon icon={faFilePdf} style={{ color: '#ff4444', fontSize: 16 }} />
                    <span style={{
                        fontSize: 13, color: '#fff', fontWeight: 500,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: '60vw',
                    }}>
                        {name || 'Документ PDF'}
                    </span>
                </div>
                <a
                    href={src}
                    download={name || 'document.pdf'}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        padding: '5px 12px', borderRadius: 6,
                        background: 'rgba(255,255,255,0.12)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        color: '#fff', fontSize: 12, textDecoration: 'none',
                        cursor: 'pointer',
                    }}
                >
                    <FontAwesomeIcon icon={faDownload} style={{ fontSize: 12 }} />
                    Завантажити
                </a>
            </div>

            {/* Спіннер поки завантажується */}
            {loading && !error && (
                <div style={{
                    position: 'absolute', inset: 0, top: 44,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    background: '#525659', zIndex: 1,
                    gap: 12,
                }}>
                    <FontAwesomeIcon icon={faSpinner}
                        style={{ fontSize: 28, color: '#fff', opacity: 0.6, animation: 'spin 1s linear infinite' }} />
                    <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>Завантаження PDF...</span>
                </div>
            )}

            {/* Помилка — браузер не підтримує вбудований PDF */}
            {error && (
                <div style={{
                    flex: 1, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: 12, color: 'rgba(255,255,255,0.7)',
                }}>
                    <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: 48, color: '#ff4444', opacity: 0.7 }} />
                    <div style={{ fontSize: 14, textAlign: 'center' }}>
                        Браузер не підтримує перегляд PDF<br />
                        <span style={{ fontSize: 12, opacity: 0.6 }}>Завантажте файл щоб переглянути</span>
                    </div>
                    <a
                        href={src}
                        download={name || 'document.pdf'}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            padding: '8px 20px', borderRadius: 8,
                            background: '#ff4444', color: '#fff',
                            textDecoration: 'none', fontSize: 13, fontWeight: 600,
                            display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        <FontAwesomeIcon icon={faDownload} />
                        Завантажити PDF
                    </a>
                </div>
            )}

            {/* iframe з PDF */}
            {!error && (
                <iframe
                    src={src + '#toolbar=1&navpanes=1&scrollbar=1'}
                    title={name || 'PDF'}
                    onLoad={function() { setLoading(false); }}
                    onError={function() { setLoading(false); setError(true); }}
                    style={{
                        flex: 1, border: 'none',
                        display: loading ? 'none' : 'block',
                    }}
                />
            )}
        </div>
    );
};

// ── Спінер CSS (один раз) ────────────────────────────────────────────────────

var SPIN_INJECTED = false;
function injectSpinStyle() {
    if (SPIN_INJECTED) return;
    SPIN_INJECTED = true;
    var s = document.createElement('style');
    s.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
    document.head.appendChild(s);
}

// ── YouTube video viewer ─────────────────────────────────────────────────────

var VideoViewer = function({ src, name }) {
    // src очікується як звичайне посилання виду
    // https://www.youtube.com/watch?v=XXXXXXXXXXX — конвертуємо в embed
    var embedSrc = src;
    var m = src && src.match(/(?:v=|youtu\.be\/|embed\/)([a-zA-Z0-9_-]{11})/);
    if (m) embedSrc = 'https://www.youtube.com/embed/' + m[1] + '?autoplay=1&rel=0';

    return (
        <div style={{
            width:  'min(900px, calc(100vw - 40px))',
            height: 'min(506px, calc((100vw - 40px) * 9 / 16), calc(100vh - 120px))',
            borderRadius: 10,
            overflow: 'hidden',
            boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
            background: '#000',
            position: 'relative',
        }}>
            <iframe
                src={embedSrc}
                title={name || 'YouTube'}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
            />
            {name && (
                <div style={{
                    position: 'absolute', bottom: -32, left: 0, right: 0,
                    fontSize: 13, color: 'rgba(255,255,255,0.75)',
                    textAlign: 'center',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {name}
                </div>
            )}
        </div>
    );
};

// ── Основний компонент ────────────────────────────────────────────────────────

const PhotoViewer = function({ src, name, mtype, onClose }) {

    useEffect(function() {
        injectSpinStyle();

        var onKey = function(e) { if (e.key === 'Escape') onClose(); };
        var onDown = function(e) {
            var container = document.getElementById('pv-container');
            if (container && !container.contains(e.target)) onClose();
        };

        document.addEventListener('keydown', onKey);
        document.addEventListener('mousedown', onDown);
        return function() {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onDown);
        };
    }, []);

    var fileType = detectType(src, name, mtype);

    return (
        <div style={{
            position: 'fixed', inset: 0,
            zIndex: 99999,
            background: 'rgba(0,0,0,0.82)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 20,
        }}>
            {/* Контейнер вмісту */}
            <div
                id="pv-container"
                style={{ position: 'relative' }}
            >
                {/* Кнопка закриття — завжди у правому верхньому куті контейнера */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: fileType === 'pdf' ? 8 : 10,
                        right: (fileType === 'pdf' || fileType === 'video') ? -44 : 10,
                        zIndex: 10,
                        background: 'rgba(20,20,20,0.8)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        borderRadius: 8,
                        width: 38, height: 38,
                        cursor: 'pointer', color: '#fff', fontSize: 18,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                    }}
                    title="Закрити (Esc)"
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                {fileType === 'pdf' ? (
                    <PdfViewer src={src} name={name} />
                ) : fileType === 'video' ? (
                    <VideoViewer src={src} name={name} />
                ) : (
                    /* Фото */
                    <div style={{
                        maxWidth:  'min(960px, calc(100vw - 40px))',
                        maxHeight: 'calc(100vh - 80px)',
                        borderRadius: 10,
                        overflow: 'hidden',
                        boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
                        background: '#000',
                        position: 'relative',
                    }}>
                        <img
                            src={src}
                            alt={name || ''}
                            style={{
                                display: 'block',
                                maxWidth:  'min(960px, calc(100vw - 40px))',
                                maxHeight: 'calc(100vh - 80px)',
                                width: 'auto', height: 'auto',
                                objectFit: 'contain',
                            }}
                        />
                        {name && (
                            <div style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                background: 'rgba(0,0,0,0.55)',
                                padding: '6px 12px',
                                fontSize: 12, color: 'rgba(255,255,255,0.85)',
                                textAlign: 'center',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                                {name}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Підказка */}
            <div style={{
                marginTop: 12, fontSize: 12,
                color: 'rgba(255,255,255,0.3)',
                pointerEvents: 'none',
            }}>
                Esc або клік поза вмістом — закрити
            </div>
        </div>
    );
};

export default PhotoViewer;